import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  LayoutDashboard, Users, Calendar, Wallet, AlertCircle, FileText, Package as PackageIcon,
  Download, TrendingUp, RefreshCw, Loader2,
} from "lucide-react";

const fmtIDR = (n) => `Rp ${new Intl.NumberFormat("id-ID").format(Math.round(n || 0))}`;
const fmtNum = (n) => new Intl.NumberFormat("id-ID").format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

// Brand palette (from index.css)
const COLORS_PIE = ["#E8A929", "#1a1a1a", "#c48f1e", "#5C5B57", "#8b6c1c", "#F5D08A", "#3a3a3a", "#c9b78a"];

function StatCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
  const toneMap = {
    primary:  "bg-primary text-white",
    accent:   "bg-accent text-primary",
    success:  "bg-emerald-600 text-white",
    warning:  "bg-amber-500 text-white",
    danger:   "bg-red-600 text-white",
    muted:    "bg-muted text-primary",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex gap-4" data-testid={testid}>
      <div className={`h-11 w-11 shrink-0 rounded-lg grid place-items-center ${toneMap[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="font-serif text-2xl text-primary font-semibold mt-1 truncate">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

export default function DashboardTab() {
  const { authHeaders } = useAuth();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [exporting, setExporting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dashboard-stats", { headers: authHeaders() });
      setData(r.data);
    } catch {
      toast.error("Gagal memuat statistik");
    } finally { setLoading(false); }
  }, [authHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const exportManifest = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/bookings/manifest.csv", {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `batik-manifest_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Manifest ter-download");
    } catch (err) {
      toast.error("Gagal export manifest");
    } finally { setExporting(false); }
  };

  if (loading && !data) {
    return (
      <TabsContent value="dashboard" data-testid="tab-dashboard-content">
        <div className="py-20 text-center text-muted-foreground text-sm inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Memuat statistik...
        </div>
      </TabsContent>
    );
  }
  if (!data) return null;

  const c = data.cards || {};

  return (
    <TabsContent value="dashboard" data-testid="tab-dashboard-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-primary flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-accent" /> Dashboard Ringkasan
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Statistik operasional real-time — pendaftaran, pemasukan, dokumen &amp; distribusi paket.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportManifest}
            disabled={exporting}
            data-testid="dashboard-export-manifest"
            className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export Manifest (.csv)
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border hover:bg-muted"
            data-testid="dashboard-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Users}       label="Jamaah Aktif"       value={fmtNum(c.active_pilgrims)}       tone="primary" testid="stat-active-pilgrims" />
        <StatCard icon={Calendar}    label="Pendaftar Bln Ini"  value={fmtNum(c.new_this_month)}         tone="accent"  testid="stat-new-month" />
        <StatCard icon={Wallet}      label="Pemasukan Bln Ini"  value={fmtIDR(c.revenue_month_idr)}      hint="Verified only" tone="success" testid="stat-revenue-month" />
        <StatCard icon={AlertCircle} label="Belum Lunas"        value={fmtNum(c.unpaid_bookings)}        tone="warning" testid="stat-unpaid" />
        <StatCard icon={FileText}    label="Perlu Verifikasi"   value={fmtNum(c.pending_verifications)}  tone="danger"  testid="stat-pending-verify" />
        <StatCard icon={PackageIcon} label="Total Paket"        value={fmtNum(c.total_packages)}         tone="muted"   testid="stat-total-packages" />
      </div>

      {/* Perlu Tindakan quick-action panel */}
      {(c.pending_verifications > 0 || c.unpaid_bookings > 0) && (
        <div className="mb-6 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-5" data-testid="admin-todo-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-amber-700" />
            <h3 className="font-serif text-lg text-primary">Perlu Tindakan Segera</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {c.pending_verifications > 0 && (
              <a href="/admin/dokumen" className="flex items-center justify-between p-3 rounded-lg bg-white border border-amber-200 hover:border-amber-400 hover:shadow-sm transition-all" data-testid="todo-docs">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Dokumen Menunggu</p>
                  <p className="font-serif text-2xl text-primary mt-1">{c.pending_verifications} <span className="text-sm text-muted-foreground">item</span></p>
                </div>
                <FileText className="h-8 w-8 text-amber-600" />
              </a>
            )}
            {c.unpaid_bookings > 0 && (
              <a href="/admin/pembayaran" className="flex items-center justify-between p-3 rounded-lg bg-white border border-red-200 hover:border-red-400 hover:shadow-sm transition-all" data-testid="todo-payment">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Bukti Bayar Pending</p>
                  <p className="font-serif text-2xl text-primary mt-1">{c.unpaid_bookings} <span className="text-sm text-muted-foreground">jamaah</span></p>
                </div>
                <Wallet className="h-8 w-8 text-red-600" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Charts row 1: trend + revenue */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-card border border-border rounded-xl p-5" data-testid="chart-registration-trend">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline text-primary">Tren 12 Bulan</p>
              <h3 className="font-serif text-lg text-primary mt-0.5 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-accent" /> Pendaftaran Jamaah</h3>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data.trend_12m} margin={{ top: 5, right: 15, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6dfd0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5C5B57" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5C5B57" }} allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} pendaftar`, "Total"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="registrations" stroke="#1a1a1a" strokeWidth={2.5} dot={{ r: 3, fill: "#E8A929" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5" data-testid="chart-revenue-trend">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline text-primary">Tren 12 Bulan</p>
              <h3 className="font-serif text-lg text-primary mt-0.5 flex items-center gap-2"><Wallet className="h-4 w-4 text-accent" /> Pemasukan (Rp)</h3>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={data.trend_12m} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6dfd0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#5C5B57" }} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#5C5B57" }}
                  tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v}
                />
                <Tooltip formatter={(v) => [fmtIDR(v), "Pemasukan"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="revenue_idr" fill="#E8A929" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 2: pie + seat availability */}
      <div className="grid lg:grid-cols-5 gap-5 mb-5">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5" data-testid="chart-package-distribution">
          <div className="mb-3">
            <p className="overline text-primary">Distribusi</p>
            <h3 className="font-serif text-lg text-primary mt-0.5">Paket Terlaris</h3>
          </div>
          {data.package_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">Belum ada booking.</p>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.package_distribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => `${entry.value}`}
                    labelLine={false}
                  >
                    {data.package_distribution.map((_, i) => (
                      <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} booking`, n]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 bg-card border border-border rounded-xl p-5" data-testid="seat-availability-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="overline text-primary">Kapasitas</p>
              <h3 className="font-serif text-lg text-primary mt-0.5">Seat Tersisa — 20 Keberangkatan Terdekat</h3>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[280px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40 sticky top-0">
                <tr>
                  <th className="p-2 text-left font-medium">Tgl</th>
                  <th className="p-2 text-left font-medium">Paket</th>
                  <th className="p-2 text-left font-medium">Airline</th>
                  <th className="p-2 text-right font-medium">Seat</th>
                </tr>
              </thead>
              <tbody>
                {data.seat_availability.map((p) => {
                  const seats = p.seats_remaining;
                  const cls = seats == null ? "text-muted-foreground" : seats <= 0 ? "text-red-600 font-semibold" : seats <= 5 ? "text-orange-600 font-semibold" : "text-emerald-700 font-semibold";
                  return (
                    <tr key={p.id} className="border-t border-border" data-testid={`seat-row-${p.id}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(p.departure_date)}</td>
                      <td className="p-2 max-w-xs truncate" title={p.name_id}>{p.name_id}</td>
                      <td className="p-2 text-xs text-muted-foreground">{p.airline || "—"}</td>
                      <td className={`p-2 text-right ${cls}`}>{seats != null ? seats : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row 3: recent activity */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="recent-activity-panel">
        <div className="mb-3">
          <p className="overline text-primary">Aktivitas</p>
          <h3 className="font-serif text-lg text-primary mt-0.5">10 Pendaftaran Terbaru</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40">
              <tr>
                <th className="p-2 text-left font-medium">Booking #</th>
                <th className="p-2 text-left font-medium">Jamaah</th>
                <th className="p-2 text-left font-medium">Paket</th>
                <th className="p-2 text-left font-medium">Status</th>
                <th className="p-2 text-left font-medium">Bayar</th>
                <th className="p-2 text-left font-medium">Waktu</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_activity.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`activity-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs">{r.booking_number || r.id.slice(0, 8)}</td>
                  <td className="p-2">{r.name}</td>
                  <td className="p-2 text-xs text-muted-foreground truncate max-w-xs" title={r.package_name}>{r.package_name}</td>
                  <td className="p-2 text-xs">{r.status}</td>
                  <td className="p-2 text-xs">{r.payment_status || "unpaid"}</td>
                  <td className="p-2 text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
              {data.recent_activity.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">Belum ada pendaftaran.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </TabsContent>
  );
}
